import { FormControl } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import settings from '@/constants/constants';
import { FF_ENABLE_GOOGLE_PLACES } from '@/constants/featureFlags';
import useFeatureFlags from '@/hooks/useFeatureFlags';

import { FormElements } from '../FormType';

import type { AddressDataType } from '@montugroup/design-system';
import { LocationInput as DsLocationInput } from '@montugroup/design-system';
import { PARCEL_LOCKER_ERROR_MESSAGE, validateParcelLocker } from '@montugroup/utilities';
import Input from './Input';

export interface LocationInputProps {
  name: string;
  label: string;
}

export const LocationInput = ({ name, label }: LocationInputProps) => {
  const { flags } = useFeatureFlags();
  const isGooglePlacesEnabled = flags[FF_ENABLE_GOOGLE_PLACES];
  const { control, setValue: setFormValue, setError, trigger } = useFormContext();

  const handleSelectedAddress = (onChange: (value: string) => void, addressData: AddressDataType) => {
    const streetLine = [addressData.subpremise, addressData.street_number, addressData.address]
      .filter(Boolean)
      .join(' ');
    onChange(streetLine);

    setFormValue('address.suburb', addressData.suburb || addressData.city, { shouldValidate: true });
    setFormValue('address.state', addressData.state, { shouldValidate: true });
    setFormValue('address.postcode', addressData.postcode, { shouldValidate: true });

    if (!addressData.street_number) {
      setError(name, { type: 'custom', message: 'Invalid address, please type street number.' });
    } else {
      trigger(name);
    }
  };

  const handleReset = (onChange: (value: string) => void) => {
    onChange('');
    setFormValue('address.suburb', '', { shouldValidate: true });
    setFormValue('address.state', '', { shouldValidate: true });
    setFormValue('address.postcode', '', { shouldValidate: true });
    trigger(name);
  };

  return (
    <FormControl sx={{ my: 2 }} fullWidth>
      <Controller
        rules={{
          required: 'This field is required',
          validate: {
            isParcelLocker: (value) => validateParcelLocker(value) || PARCEL_LOCKER_ERROR_MESSAGE,
          },
        }}
        control={control}
        defaultValue=""
        name={name}
        render={({ field: { onChange, value }, fieldState: { error } }) =>
          isGooglePlacesEnabled ? (
            <DsLocationInput
              id={name}
              name={name}
              label={label}
              variant="filled"
              value={value}
              googlePlacesApiKey={settings.googlePlacesApiKey}
              error={error?.message}
              onSelectedAddress={(addressData) => handleSelectedAddress(onChange, addressData)}
              onReset={() => handleReset(onChange)}
            />
          ) : (
            <Input type={FormElements.input} name={name} label={label} />
          )
        }
      />
    </FormControl>
  );
};
